/**
 * Spatial Context — Home map glass workspace (Wave C1 + IA-R8 rename).
 * Soft-honest surfaces: Observation → Recommendation → Assessment → Decision →
 * Execution · History · Evidence · Timeline · Workspace chrome.
 * Decision Allow/Hold uses shared data-ai-home-* attrs only.
 * Wave E: do not hard-truncate plan text — sheet scrolls for full info.
 */

import { softOperatorNote, softDeviceLabel } from "./format.js";
import { decisionSurfaceActionsHtml } from "./decision-surface.js";
import { incidentWorkspaceSpineHtml } from "./incident-workspace-chrome.js";
import { evidenceGraphSoftHtml, evidenceTimelineSoftHtml } from "./evidence-graph-soft.js";

const TABS = [
  { id: "observation", label: "Observe" },
  { id: "recommendation", label: "Recommend" },
  { id: "assessment", label: "Assess" },
  { id: "decision", label: "Decide" },
  { id: "execution", label: "Execute" },
  { id: "frame", label: "Frame" },
  { id: "history", label: "History" },
  { id: "evidence", label: "Evidence" },
  { id: "timeline", label: "Timeline" },
  { id: "workspace", label: "Workspace" },
];

function esc(fn, s) {
  if (typeof fn === "function") return fn(s);
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function softNote(raw) {
  try {
    return softOperatorNote(String(raw || ""));
  } catch (_) {
    return String(raw || "").trim();
  }
}

function priorityBand(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return "Priority · review";
  if (n >= 85) return "Priority · critical";
  if (n >= 70) return "Priority · high";
  if (n >= 40) return "Priority · medium";
  return "Priority · low";
}

function planIdOf(plan) {
  return String((plan && (plan.incident_id || plan.id || plan.item_id)) || "").trim();
}

function fmtSoftTs(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "number" && Number.isFinite(v)) {
    const ms = v > 1e12 ? v : v * 1000;
    try {
      return new Date(ms).toISOString().replace("T", " ").slice(0, 19) + "Z";
    } catch (_) {
      return String(v);
    }
  }
  const s = String(v).trim();
  return s.length > 24 ? s.slice(0, 24) : s || "—";
}

/** Haversine meters — Soft spatial ranking only (not Live SRF). */
function softHaversineM(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (Number(d) * Math.PI) / 180;
  const R = 6371000;
  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lng2 - lng1);
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function fmtSoftDist(m) {
  if (!Number.isFinite(m)) return "";
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

function fieldRow(escapeHtml, k, v) {
  const val = softNote(v);
  if (!val) return "";
  return `<div class="croc-saw__field"><span class="croc-saw__fk">${esc(escapeHtml, k)}</span><span class="croc-saw__fv">${esc(escapeHtml, val)}</span></div>`;
}

/**
 * @param {object} plan
 * @param {{
 *   escapeHtml?: (s: unknown) => string,
 *   activeTab?: string,
 *   detail?: object | null,
 *   history?: object[],
 *   fieldCandidates?: Array<{ kind?: string, label?: string, id?: string, meters?: number, stale?: boolean, note?: string }>,
 *   candidatesNote?: string,
 * }} [ctx]
 */
export function buildSpatialAiWorkbenchHtml(plan, ctx = {}) {
  const escapeHtml = ctx.escapeHtml;
  const active = String(ctx.activeTab || "decision").trim() || "decision";
  const detail = ctx.detail && typeof ctx.detail === "object" ? ctx.detail : null;
  const d = detail || plan || {};
  const fieldCandidates = Array.isArray(ctx.fieldCandidates) ? ctx.fieldCandidates : [];
  const candidatesNote = softNote(ctx.candidatesNote || "");
  const id = planIdOf(plan);
  const deviceId = String((plan && plan.device_id) || (d && d.device_id) || "").trim();
  const deviceLabel = softNote(
    d.display_label || d.device_label || plan?.display_label || plan?.device_label || "",
  );
  const deviceShow = (() => {
    if (deviceLabel && deviceLabel !== "—") return deviceLabel;
    try {
      const soft = softDeviceLabel(deviceId);
      if (soft && soft !== "—") return soft;
    } catch (_) {}
    return deviceId || "This device";
  })();
  const eventId = String(
    (d && (d.event_id != null ? d.event_id : d.incident_event_id)) ||
      (plan && plan.event_id) ||
      "",
  ).trim();
  const risk = d.risk_score != null ? d.risk_score : d.risk != null ? d.risk : plan && plan.risk_score != null ? plan.risk_score : plan && plan.risk;
  const riskNum = Number(risk);
  const band = priorityBand(risk);
  const status = softNote(d.status || plan?.status || "open");
  const summary = softNote(
    d.summary || d.title || plan?.summary || plan?.title || plan?.recommendation || "Auto plan",
  );
  const category = softNote(
    d.category || d.classification || d.alarm_class || plan?.category || "Security review",
  );
  const recommendation = softNote(
    d.recommendation ||
      d.suggested_action ||
      (Array.isArray(d.recommended_actions) && d.recommended_actions.length
        ? d.recommended_actions.join(", ")
        : "") ||
      (Array.isArray(d.actions) && d.actions.length ? d.actions.join(", ") : "") ||
      "Sentinel will continue the auto plan unless you Hold.",
  );
  const reasons = Array.isArray(d.reason_json)
    ? d.reason_json
    : Array.isArray(plan?.reason_json)
      ? plan.reason_json
      : Array.isArray(d.reason)
        ? d.reason
        : Array.isArray(d.reasons)
          ? d.reasons
          : Array.isArray(plan?.reasons)
            ? plan.reasons
            : [];
  const actionList = Array.isArray(d.actions_json) && d.actions_json.length
    ? d.actions_json
    : Array.isArray(plan?.actions_json) && plan.actions_json.length
      ? plan.actions_json
      : Array.isArray(d.recommended_actions) && d.recommended_actions.length
        ? d.recommended_actions
        : Array.isArray(d.actions)
          ? d.actions
          : [];
  const reasonText = reasons.length
    ? reasons.map((r) => softNote(String(r))).filter(Boolean).join("\n")
    : softNote(
        d.detail ||
          d.enrich_summary ||
          "Rules set the priority. Written reasons appear when the model path is open.",
      );
  const modelUsed = softNote(
    d.model_used || d.enrich_model || d.model_tier || d.model || plan?.model_used || "",
  );
  const modelLive = !!(
    modelUsed &&
    !/^(stub|none|off|rules)$/i.test(modelUsed) &&
    !/^rules(\+|$)/i.test(modelUsed) &&
    !/^template$/i.test(modelUsed) &&
    !/soft/i.test(modelUsed)
  );
  const modelHybrid = /^rules\+(fast|deep)$/i.test(modelUsed);
  const enrichConfidence =
    d.enrich_confidence != null
      ? d.enrich_confidence
      : d.confidence != null
        ? d.confidence
        : null;
  const enrichNote = softNote(
    d.enrich_status ||
      d.enrich_note ||
      d.model_note ||
      (modelLive
        ? `Model enrich live · ${modelUsed}${enrichConfidence != null ? ` · confidence about ${Math.round(Number(enrichConfidence) <= 1 ? Number(enrichConfidence) * 100 : Number(enrichConfidence))}%` : ""}`
        : modelUsed === "template"
          ? "Enrich Soft fallback · short template · model path cold"
          : modelHybrid
            ? `Enrich hybrid · ${modelUsed} · rules own score`
            : modelUsed === "rules" || modelUsed.startsWith("rules")
              ? `Enrich · ${modelUsed || "rules"} · rules own score`
              : "Model enrich Soft · rules own the score"),
  );
  const autoCall = !!(d.auto_call || plan?.auto_call);
  const requireHuman = d.require_human_approval != null
    ? !!d.require_human_approval
    : d.requires_human_approval != null
      ? !!d.requires_human_approval
      : d.needs_approval != null
        ? !!d.needs_approval
        : true;
  const dispatchId = String(
    d.dispatch_id || d.responder_dispatch_id || plan?.dispatch_id || "",
  ).trim();
  const evidence =
    d.evidence && typeof d.evidence === "object"
      ? d.evidence
      : d.evidence_json && typeof d.evidence_json === "object"
        ? d.evidence_json
        : {};
  const snap =
    d.snapshot && typeof d.snapshot === "object"
      ? d.snapshot
      : plan?.snapshot && typeof plan.snapshot === "object"
        ? plan.snapshot
        : {};
  const snapshotId = String(
    snap.snapshot_id || evidence.snapshot_id || d.snapshot_id || "",
  ).trim();
  const thumbUrl = String(snap.thumb_url || snap.url || d.thumb_url || "").trim();
  const _visionDetections = Array.isArray(snap.vision_detections)
    ? snap.vision_detections
    : Array.isArray(evidence.vision_detections)
      ? evidence.vision_detections
      : Array.isArray(d.vision_detections)
        ? d.vision_detections
        : [];
  const visionSummary = softNote(
    evidence.vision_summary || snap.vision_summary || d.vision_summary || "",
  );
  const visionAnalyzed = !!(evidence.vision_analyzed || snap.vision_analyzed || d.vision_analyzed);
  const visionModel = softNote(
    evidence.vision_model || snap.vision_model || d.vision_model || "",
  );
  const visionLine = visionAnalyzed && visionSummary
    ? visionSummary
    : visionAnalyzed
      ? `(${visionModel || "vision"} analyzed · no text)`
      : "Soft · no box detection yet";
  const visionDialogue = Array.isArray(evidence.vision_dialogue)
    ? evidence.vision_dialogue
    : Array.isArray(d.vision_dialogue)
      ? d.vision_dialogue
      : [];
  const visionLastAsk = softNote(evidence.vision_last_ask || d.vision_last_ask || "");
  const history = Array.isArray(ctx.history) ? ctx.history : [];
  const riskLine = Number.isFinite(riskNum) ? `${band} · score ${Math.round(riskNum)}` : band;
  const statusLc = String(status || "").toLowerCase();
  const isPending = !statusLc || statusLc === "pending" || statusLc === "open";
  const approvedBy = softNote(d.approved_by || d.held_by || "");
  const timelineSteps = [];
  if (d.created_at != null && d.created_at !== "") {
    timelineSteps.push({ k: "Plan created", v: fmtSoftTs(d.created_at) });
  }
  timelineSteps.push({
    k: "Rules triage",
    v: Number.isFinite(riskNum) ? `score ${Math.round(riskNum)} · ${category}` : category || "scored",
  });
  timelineSteps.push({
    k: "Model enrich",
    v: modelLive
      ? `${modelUsed} Live`
      : modelHybrid
        ? `${modelUsed} hybrid`
        : modelUsed || "rules Soft",
  });
  if (visionAnalyzed || visionSummary) {
    timelineSteps.push({
      k: "Vision line",
      v: visionAnalyzed ? `${visionModel || "frame text"} · one-liner` : "Soft · waiting",
    });
  }
  if (isPending) {
    timelineSteps.push({
      k: "On the loop",
      v: requireHuman ? "Needs you · Hold / Takeover" : "Team autonomous path",
    });
  } else {
    timelineSteps.push({
      k: statusLc === "rejected" || statusLc === "held" ? "Held" : "Continued",
      v: `${fmtSoftTs(d.approved_at)}${approvedBy ? ` · ${approvedBy}` : ""}`,
    });
  }
  if (dispatchId) {
    timelineSteps.push({ k: "Dispatch", v: dispatchId });
  } else if (!isPending && autoCall) {
    timelineSteps.push({ k: "Dispatch", v: "auto-call path · check Assignments" });
  }
  if (snapshotId || thumbUrl) {
    timelineSteps.push({ k: "Evidence", v: snapshotId || "snapshot linked" });
  }

  const aiSpine = `
    <div class="croc-saw__spine" aria-label="AI Context spine">
      <div class="croc-saw__spine-item">
        <span class="croc-saw__spine-k">Rules</span>
        <span class="croc-saw__spine-v">${esc(escapeHtml, Number.isFinite(riskNum) ? `score ${Math.round(riskNum)}` : "triage")}</span>
      </div>
      <div class="croc-saw__spine-item">
        <span class="croc-saw__spine-k">Model</span>
        <span class="croc-saw__spine-v">${esc(escapeHtml, modelUsed || "stub Soft")}</span>
      </div>
      <div class="croc-saw__spine-item">
        <span class="croc-saw__spine-k">Human</span>
        <span class="croc-saw__spine-v">${esc(escapeHtml, requireHuman ? "gate on" : "auto path")}</span>
      </div>
      <div class="croc-saw__spine-item">
        <span class="croc-saw__spine-k">Field</span>
        <span class="croc-saw__spine-v">${esc(escapeHtml, dispatchId ? "dispatch ready" : autoCall ? "auto armed" : "await decide")}</span>
      </div>
    </div>`;

  const tabBtns = TABS.map((t) => {
    const on = t.id === active ? " is-active" : "";
    return `<button type="button" class="croc-saw__tab btn sm ghost btn-tap${on}" data-saw-tab="${esc(escapeHtml, t.id)}" aria-pressed="${t.id === active ? "true" : "false"}">${esc(escapeHtml, t.label)}</button>`;
  }).join("");

  const fieldCandidateHtml = fieldCandidates.length
    ? `<div class="croc-saw__fields">${fieldCandidates
        .slice(0, 6)
        .map((c, i) => {
          const kind = softNote(c.kind || "candidate");
          const lab = softNote(c.label || c.id || `Candidate ${i + 1}`);
          const dist = fmtSoftDist(Number(c.meters));
          const stale = c.stale ? " · stale GPS" : "";
          const note = softNote(c.reason || c.note || "");
          const rank = Number(c.rank) > 0 ? Number(c.rank) : i + 1;
          return fieldRow(
            escapeHtml,
            `${kind} #${rank}`,
            `${lab}${dist ? ` · ${dist}` : ""}${stale}${note ? ` · ${note}` : ""}`,
          );
        })
        .join("")}</div>
      <p class="honest croc-saw__note">${
        fieldCandidates.some((c) => String(c.source || "").includes("server_topn"))
          ? "Fresh-only server Top-N · shared map visibility · Soft geometry — not fabric reach."
          : "Fresh-only Soft ranking from shared GPS / Home map — fabric reach not live."
      }</p>`
    : `<p class="muted croc-saw__s">${esc(
        escapeHtml,
        candidatesNote === "no_alarm_location" || candidatesNote === "no_fresh_responder"
          ? softNote(candidatesNote)
          : "No shared phone GPS near this board yet — open Assignments or wait for responders to share location.",
      )}</p>
      <p class="honest croc-saw__note">Haversine Soft Top-N — not Live SRF QUERY fabric. Empty list is honest, not a silent dispatch.</p>`;

  /** @type {Record<string, string>} */
  const bodies = {
    observation: `
      <p class="croc-saw__k">Observation</p>
      <p class="croc-saw__v">${esc(escapeHtml, category)}</p>
      <p class="croc-saw__full">${esc(escapeHtml, summary)}</p>
      <div class="croc-saw__fields">
        ${fieldRow(escapeHtml, "Plan", id || "—")}
        ${fieldRow(escapeHtml, "Status", status)}
        ${fieldRow(escapeHtml, "Board", deviceShow)}
        ${fieldRow(escapeHtml, "Event", eventId || "—")}
        ${fieldRow(escapeHtml, "Priority", riskLine)}
        ${fieldRow(escapeHtml, "AI model", modelUsed || "rules Soft")}
      </div>
      <p class="honest croc-saw__note">Notify-first · spatial context from Home map — Soft observation · vision one-liner is Assessment/Evidence.</p>`,
    recommendation: `
      <p class="croc-saw__k">Recommendation</p>
      <p class="croc-saw__full">${esc(escapeHtml, recommendation)}</p>
      ${actionList.length
        ? `<div class="croc-saw__fields">${actionList
            .slice(0, 8)
            .map((a, i) => fieldRow(escapeHtml, `Action ${i + 1}`, a))
            .join("")}</div>`
        : ""}
      <p class="honest croc-saw__note">Rules + enrich suggestion · human still decides on Decide · ${esc(escapeHtml, enrichNote)}</p>`,
    assessment: `
      <p class="croc-saw__k">Assessment</p>
      <p class="croc-saw__v">${esc(escapeHtml, riskLine)}</p>
      <p class="croc-saw__full croc-saw__pre">${esc(escapeHtml, reasonText)}</p>
      <div class="croc-saw__fields">
        ${fieldRow(escapeHtml, "Board", deviceShow)}
        ${fieldRow(escapeHtml, "Category", category)}
        ${fieldRow(escapeHtml, "Status", status)}
        ${fieldRow(escapeHtml, "Rules score", Number.isFinite(riskNum) ? String(Math.round(riskNum)) : "—")}
        ${fieldRow(escapeHtml, "Model tier", modelUsed || "rules Soft")}
        ${fieldRow(escapeHtml, "Enrich", enrichNote)}
        ${enrichConfidence != null
          ? fieldRow(
            escapeHtml,
            "Enrich conf",
            `${Math.round(Number(enrichConfidence) <= 1 ? Number(enrichConfidence) * 100 : Number(enrichConfidence))}% Soft`,
          )
          : ""}
        ${fieldRow(escapeHtml, "Vision line", visionLine)}
        ${fieldRow(escapeHtml, "On the loop", requireHuman ? "Needs you" : "Team autonomous")}
        ${fieldRow(escapeHtml, "Auto call", autoCall ? "armed" : "off")}
      </div>
      ${
        visionAnalyzed && visionSummary
          ? `<p class="croc-saw__full" style="margin-top:8px">${esc(escapeHtml, visionSummary)}</p>`
          : ""
      }
      <p class="honest croc-saw__note">Soft Why (MAIC) · rules own the number · ${
        modelLive ? `enrich Live (${esc(escapeHtml, modelUsed)})` : "enrich Soft"
      } · ${
        visionAnalyzed
          ? `frame text (${esc(escapeHtml, visionModel || "ocr")}) · no boxes`
          : "frame Soft · no boxes"
      } · fabric reach not live.</p>`,
    decision: `
      <p class="croc-saw__k">Decision · Hold / Takeover</p>
      <p class="croc-saw__full">${esc(escapeHtml, summary)}</p>
      ${
        isPending
          ? `<p class="muted croc-saw__s">Tonight's next step: Hold · Takeover — same interrupt as Home.</p>
      <div class="croc-saw__fields">
        ${fieldRow(escapeHtml, "Board", deviceShow)}
        ${fieldRow(escapeHtml, "Why", reasonText)}
        ${fieldRow(escapeHtml, "Interrupt", requireHuman ? "Needs you" : "Team may auto-continue")}
        ${fieldRow(escapeHtml, "Auto call", autoCall ? "will dispatch after continue" : "notify-first only")}
        ${fieldRow(escapeHtml, "Status", status || "pending")}
      </div>
      <div class="croc-saw__acts">
        ${decisionSurfaceActionsHtml({ incidentId: id, context: "spatial", compact: true, waiting: true })}
      </div>
      <p class="honest croc-saw__note">One next step · box detection / map vision / fabric reach not live.</p>`
          : `<p class="muted croc-saw__s">This plan is already decided — interrupt closed. Open Execute for field follow-up.</p>
      <div class="croc-saw__fields">
        ${fieldRow(escapeHtml, "Board", deviceShow)}
        ${fieldRow(escapeHtml, "Status", status || "—")}
        ${fieldRow(escapeHtml, "By", approvedBy || "—")}
        ${fieldRow(escapeHtml, "When", fmtSoftTs(d.approved_at))}
        ${fieldRow(escapeHtml, "Dispatch", dispatchId || (autoCall ? "check Assignments" : "none"))}
      </div>
      <p class="honest croc-saw__note">No second gate · AI Policy stays policy &amp; history.</p>`
      }`,
    execution: `
      <p class="croc-saw__k">Execution</p>
      <p class="muted croc-saw__s">Field follow-up after Decision — Soft nearest people/devices, then assign · dispatch.</p>
      <div class="croc-saw__fields">
        ${fieldRow(escapeHtml, "Board", deviceShow)}
        ${fieldRow(escapeHtml, "Event", eventId || "—")}
        ${fieldRow(escapeHtml, "Dispatch", dispatchId || (autoCall ? "pending Allow" : "not created"))}
        ${fieldRow(
          escapeHtml,
          "AI spine",
          `Rules → Enrich ${modelLive ? "Live" : modelHybrid ? "hybrid" : "Soft"} → Human → Field ${dispatchId || autoCall ? "armed" : "Soft"}`,
        )}
      </div>
      <p class="croc-saw__k" style="margin-top:10px">Soft field candidates</p>
      ${fieldCandidateHtml}
      <div class="croc-saw__acts">
        <button type="button" class="btn sm btn-tap" data-cc-go="response">Assignments</button>
        ${dispatchId ? `<button type="button" class="btn sm secondary btn-tap" data-cc-go="response?dispatch=${encodeURIComponent(dispatchId)}">Open dispatch</button>` : ""}
        ${eventId ? `<button type="button" class="btn sm ghost btn-tap" data-cc-go="orchestration?event_id=${encodeURIComponent(eventId)}">Response team</button>` : `<button type="button" class="btn sm ghost btn-tap" data-cc-go="orchestration">Response team</button>`}
      </div>`,
    frame: `
      <p class="croc-saw__k">Frame · map shell</p>
      <p class="muted croc-saw__s">Ask about the linked camera frame · scene text Soft · no live boxes.</p>
      ${
        thumbUrl
          ? `<div class="croc-saw__shot"><img src="${esc(escapeHtml, thumbUrl)}" alt="Frame preview" loading="lazy" /></div>`
          : `<p class="muted croc-saw__s">No thumb yet — Refresh OCR after snapshot lands.</p>`
      }
      <div class="croc-saw__fields">
        ${fieldRow(escapeHtml, "Snapshot", snapshotId || "none yet")}
        ${fieldRow(escapeHtml, "OCR line", visionLine)}
        ${visionLastAsk ? fieldRow(escapeHtml, "Last ask", visionLastAsk) : ""}
      </div>
      ${
        visionDialogue.length
          ? `<ul class="croc-saw__list">${visionDialogue
              .slice(-4)
              .map((turn) => {
                const qq = softNote(turn && turn.q);
                const aa = softNote(turn && turn.a);
                return `<li><span class="muted">Q</span> ${esc(escapeHtml, qq)}<br/><span class="muted">A</span> ${esc(escapeHtml, aa)}</li>`;
              })
              .join("")}</ul>`
          : `<p class="muted croc-saw__s">No frame questions yet.</p>`
      }
      <div class="croc-saw__acts" style="flex-wrap:wrap;gap:8px;align-items:center">
        <input type="text" class="input sm" data-saw-frame-q="${esc(escapeHtml, id)}" maxlength="280" placeholder="Ask about this frame…" style="flex:1;min-width:12rem" />
        <button type="button" class="btn sm btn-tap" data-saw-frame-ask="${esc(escapeHtml, id)}">Ask</button>
        <button type="button" class="btn sm ghost btn-tap" data-saw-frame-refresh="${esc(escapeHtml, id)}">Refresh OCR</button>
      </div>
      <p class="honest croc-saw__note">Map frame ask · risk score unchanged · box detection and fabric reach not live.</p>`,
    history: `
      <p class="croc-saw__k">History</p>
      ${
        history.length
          ? `<ul class="croc-saw__list">${history
              .slice(0, 12)
              .map((h) => {
                const hid = planIdOf(h);
                const st = softNote(h.status || "open");
                const lab = softNote(h.summary || h.title || hid || "Plan");
                return `<li><button type="button" class="croc-saw__hist btn ghost sm btn-tap" data-saw-open-plan="${esc(escapeHtml, hid)}"><span class="croc-saw__hist-lab">${esc(escapeHtml, lab)}</span> <span class="muted">· ${esc(escapeHtml, st)}</span></button></li>`;
              })
              .join("")}</ul>`
          : `<p class="muted croc-saw__s">No other waiting plans for this board right now.</p>`
      }`,
    evidence: `
      <p class="croc-saw__k">Evidence</p>
      <p class="muted croc-saw__s">${
        visionAnalyzed
          ? "Camera snapshot + scene text one-liner · no live boxes."
          : "Camera / snapshot chain — Vision Soft (AI_VISION_ENABLED) · needs an image for OCR; open Observation for photos."
      }</p>
      ${
        thumbUrl
          ? `<div class="croc-saw__shot"><img src="${esc(escapeHtml, thumbUrl)}" alt="Snapshot preview" loading="lazy" /></div>`
          : ""
      }
      ${
        visionAnalyzed && visionSummary
          ? `<p class="croc-saw__full">${esc(escapeHtml, visionSummary)}</p>`
          : ""
      }
      <div class="croc-saw__fields">
        ${fieldRow(escapeHtml, "Event", eventId || "—")}
        ${fieldRow(escapeHtml, "Plan", id || "—")}
        ${fieldRow(escapeHtml, "Snapshot", snapshotId || (thumbUrl ? "linked" : "none yet"))}
        ${fieldRow(escapeHtml, "Vision line", visionLine)}
      </div>
      <div class="croc-saw__acts">
        <button type="button" class="btn sm btn-tap" data-cc-go="snapshots"${eventId ? ` data-cc-id="${esc(escapeHtml, eventId)}"` : ""}>Evidence photos</button>
        <button type="button" class="btn sm secondary btn-tap" data-cc-go="account/cameras">Cameras</button>
        ${eventId ? `<button type="button" class="btn sm ghost btn-tap" data-cc-go="incidents" data-cc-id="${esc(escapeHtml, eventId)}">Case · proof</button>` : `<button type="button" class="btn sm ghost btn-tap" data-cc-go="audit-log">Proof</button>`}
      </div>
      ${evidenceGraphSoftHtml({ eventId, deviceId, snapshotId, incidentId: id, compact: true })}`,
    timeline: `
      <p class="croc-saw__k">Timeline</p>
      <p class="muted croc-saw__s">Soft plan spine on this card — case desk holds full incident loop history.</p>
      <div class="croc-saw__fields">
        ${timelineSteps.map((s) => fieldRow(escapeHtml, s.k, s.v)).join("")}
        ${fieldRow(escapeHtml, "Event", eventId || "—")}
        ${fieldRow(escapeHtml, "Plan", id || "—")}
      </div>
      <div class="croc-saw__acts">
        ${eventId ? `<button type="button" class="btn sm btn-tap" data-cc-go="incidents" data-cc-id="${esc(escapeHtml, eventId)}">Open case timeline</button>` : `<button type="button" class="btn sm btn-tap" data-cc-go="incidents">Open cases</button>`}
        <button type="button" class="btn sm ghost btn-tap" data-cc-go="auto-sentinel"${id ? ` data-cc-id="${esc(escapeHtml, id)}"` : ""}>Policy history</button>
      </div>
      ${evidenceTimelineSoftHtml({ steps: timelineSteps, eventId })}`,
    workspace: `
      <p class="croc-saw__k">Workspace</p>
      <p class="croc-saw__full">One place on the map. Spine: Rules → Enrich ${
        modelLive ? `Live (${esc(escapeHtml, modelUsed)})` : modelHybrid ? `hybrid (${esc(escapeHtml, modelUsed)})` : `Soft (${esc(escapeHtml, modelUsed || "rules")})`
      } → Continue or Hold → Field${visionAnalyzed ? " · Frame text" : ""}. Policy stays policy and history only.</p>
      <p class="honest croc-saw__note">${esc(escapeHtml, enrichNote)}</p>
      <div class="croc-saw__acts">
        <button type="button" class="btn sm secondary btn-tap" data-cc-go="auto-sentinel"${id ? ` data-cc-id="${esc(escapeHtml, id)}"` : ""}>Policy · this plan</button>
        <button type="button" class="btn sm ghost btn-tap" data-cc-go="auto-sentinel">Policy</button>
      </div>`,
  };

  const body = bodies[active] || bodies.decision;

  return `
    <div class="croc-saw" data-spatial-ai-workbench="1" data-spatial-context="1" data-incident-workspace="1" data-incident-engine="1" data-plan-id="${esc(escapeHtml, id)}" data-active-tab="${esc(escapeHtml, active)}">
      ${incidentWorkspaceSpineHtml({
        activeId: active === "observation" || active === "assessment" || active === "recommendation" || active === "decision" || active === "execution" || active === "evidence" || active === "timeline"
          ? (active === "observation" ? "observation"
            : active === "assessment" || active === "frame" ? "assessment"
            : active === "recommendation" ? "recommendation"
            : active === "decision" ? "decision"
            : active === "execution" ? "execution"
            : active === "evidence" ? "evidence"
            : active === "timeline" || active === "history" ? "timeline"
            : "decision")
          : "decision",
        incidentId: id,
        title: summary || "Tonight's plan",
      })}
      <div class="croc-cc-ai-map-sheet__head">
        <span class="badge warn">On the map</span>
        <button type="button" class="btn sm ghost btn-tap" data-ai-map-sheet-close="1" aria-label="Close">Close</button>
      </div>
      <p class="croc-cc-ai-map-sheet__title croc-saw__title-full">${esc(escapeHtml, summary || "Auto plan")}</p>
      <p class="muted croc-cc-ai-map-sheet__meta">${esc(escapeHtml, riskLine)} · ${esc(escapeHtml, deviceShow)}${id ? ` · ${esc(escapeHtml, id)}` : ""}</p>
      ${aiSpine}
      <div class="croc-saw__tabs" role="tablist" aria-label="On the map">${tabBtns}</div>
      <div class="croc-saw__body" data-saw-body="1">${body}</div>
    </div>`;
}

export const SPATIAL_AI_TABS = TABS;
export { softHaversineM, fmtSoftDist };
